import type {
  AxiosError,
  CanceledError,
} from 'axios';
import axios from 'axios';
import {
  useContext,
  useEffect,
} from 'react';
import { useLocation } from 'react-router';
import { useAppAuth } from 'shared/hooks/auth/useAppAuth.js';
import { authRoutes } from 'shared/routes.js';
import { ErrorStatus } from 'shared/utils/enums.js';
import { getMultiModalConfig } from 'shared/utils/multiModalConfig.js';

import { ClearCacheContext } from './hooks/useClearCache.js';

declare global {
  interface Window {
    config: {
      BACKEND_URL: string;
    };
  }
}

export const useInitializeApiConfig = () => {
  const { setShouldClearCache } = useContext(ClearCacheContext);
  const location = useLocation();
  const { logout, isAuthenticated } = useAppAuth();

  const baseApiUrl = getMultiModalConfig()?.baseApiUrl;
  if (baseApiUrl) {
    axios.defaults.baseURL = baseApiUrl;
    axios.defaults.withCredentials = true; // required to send and set cookies cross-domain (multimodal subdomain)
  }

  useEffect(() => {
    const autoLogoutInterceptorId = axios.interceptors.response.use(
      response => response,
      (error: AxiosError | CanceledError<any>) => {
        const keepDefaultBehaviour = Promise.reject(error);

        if (!axios.isAxiosError(error)) {
          return keepDefaultBehaviour;
        }

        if (error.response?.status !== ErrorStatus.Unauthorized) {
          return keepDefaultBehaviour;
        }

        const noAuthRequired = Object.values(authRoutes).includes(
          location.pathname,
        );

        if (noAuthRequired || !isAuthenticated) {
          return keepDefaultBehaviour;
        }

        void logout();
      },
    );

    return () => {
      axios.interceptors.response.eject(autoLogoutInterceptorId);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const clearCacheInterceptor = axios.interceptors.response.use(
      response => response,
      (error: AxiosError | CanceledError<any>) => {
        const keepDefaultBehaviour = Promise.reject(error);

        if (!axios.isAxiosError(error)) {
          return keepDefaultBehaviour;
        }

        if (error.response?.status !== ErrorStatus.Forbidden) {
          return keepDefaultBehaviour;
        }

        // @TODO https://energyweb.atlassian.net/browse/SAF-1488
        setShouldClearCache(true);
      },
    );

    return () => {
      axios.interceptors.response.eject(clearCacheInterceptor);
    };
  }, []);

  useEffect(() => {
    const cloudflareTokenId = axios.interceptors.request.use(
      (config) => {
        // To properly receive 401 user when session is expired
        // https://developers.cloudflare.com/cloudflare-one/identity/users/session-management/#ajax
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(cloudflareTokenId);
    };
  }, []);
};
